import {
  IonFooter,
  IonHeader,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonTitle,
  type RefresherEventDetail,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BackButton } from "@/components/back-button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { Toolbar } from "@/components/toolbar";
import { useBoomerForm } from "@/form/boomer/hook";
import { CustomFieldFile } from "@/form/components/custom-field-file";
import { CustomFieldInput } from "@/form/components/custom-field-input";
import { CustomFieldSelect } from "@/form/components/custom-field-select";
import { CustomFieldToggle } from "@/form/components/custom-field-toggle";
import { api } from "@/lib/backend";
import type { CustomField, CustomFieldDefinition, CustomFieldValueInput } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { useMe } from "@/lib/hooks/use-me";
import { Routes } from "@/lib/router";
import { logger } from "@/logger";
import { useStatusBarHeight } from "@/utils/use-status-bar-height";

const TOAST_KEY = "update-custom-fields";

type CustomFieldFormValue = {
  [key: string]: string | number | boolean | string[] | File | null;
};

function getDefaultValueFromCustomField(field: CustomField): any {
  const { value, customFieldDefinition } = field;

  if (!value) {
    return null;
  }

  switch (customFieldDefinition.type) {
    case "TEXT":
      return value.text ?? "";
    case "NUMBER":
      return value.number ?? null;
    case "DATE":
      return value.date ?? "";
    case "SELECT":
      return value.select ?? "";
    case "MULTI_SELECT":
      return value.multiSelect ?? [];
    case "BOOLEAN":
      return value.boolean ?? false;
    case "FILE":
      // Ensure we return a string URL or null, never an object
      return typeof value.file === "string" ? value.file : null;
    default:
      return null;
  }
}

function buildCustomFieldValueInput(
  type: CustomFieldDefinition["type"],
  value: any,
): CustomFieldValueInput {
  const input: CustomFieldValueInput = {
    text: null,
    number: null,
    date: null,
    select: null,
    multiSelect: null,
    boolean: null,
  };

  switch (type) {
    case "TEXT":
      input.text = value || null;
      break;
    case "NUMBER":
      input.number = value !== null && value !== "" ? Number(value) : null;
      break;
    case "DATE":
      input.date = value || null;
      break;
    case "SELECT":
      input.select = value || null;
      break;
    case "MULTI_SELECT":
      input.multiSelect = Array.isArray(value) ? (value as string[]) : [];
      break;
    case "BOOLEAN":
      input.boolean = Boolean(value);
      break;
  }

  return input;
}

export function SettingsCustomFieldsPage() {
  const { t } = useTranslation();
  const { me } = useMe();
  const statusBarHeight = useStatusBarHeight();
  const queryClient = useQueryClient();

  const employeeId = me?.employee?.id;

  const {
    data: customFields,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["custom-fields", "EMPLOYEE", employeeId],
    queryFn: () => extractionWrapper(api.customFields.getAll("EMPLOYEE", employeeId!)),
    enabled: !!employeeId,
  });

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    event.detail.complete();
  };

  // Build default values from custom fields
  const defaultValues: CustomFieldFormValue = {};
  customFields?.forEach((field) => {
    defaultValues[field.customFieldDefinition.id] = getDefaultValueFromCustomField(field);
  });

  const updateCustomFieldMutation = useExtractedMutation({
    mutationFn: async ({
      definitionId,
      value,
      type,
    }: {
      definitionId: string;
      value: any;
      type: CustomFieldDefinition["type"];
    }) => {
      if (type === "FILE" && value instanceof File) {
        // Handle file upload
        logger.info("Uploading file", { definitionId, fileName: value.name, fileSize: value.size });
        return api.customFields.uploadFile("EMPLOYEE", employeeId!, definitionId, { file: value });
      }
      // Handle regular field update
      const input = buildCustomFieldValueInput(type, value);
      return api.customFields.update("EMPLOYEE", employeeId!, definitionId, input);
    },
    onSuccess: async () => {
      toast.success(t("actions:update-custom-fields.success"), { id: TOAST_KEY });
      await queryClient.invalidateQueries({
        queryKey: ["custom-fields", "EMPLOYEE", employeeId],
      });
    },
    onError: (error) => {
      toast.error(t("actions:update-custom-fields.error"), { id: TOAST_KEY });
      logger.error(error);
    },
  });

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      toast.loading(t("actions:update-custom-fields.loading"), { id: TOAST_KEY });

      // Update each field individually, skip FILE fields without new files
      const updatePromises = customFields
        ?.filter((field) => {
          const fieldValue = value[field.customFieldDefinition.id];
          // Skip FILE fields that don't have a new File object selected
          if (field.customFieldDefinition.type === "FILE" && !(fieldValue instanceof File)) {
            logger.info("Skipping file field - no new file selected", {
              definitionId: field.customFieldDefinition.id,
            });
            return false;
          }
          return true;
        })
        .map((field) => {
          const definitionId = field.customFieldDefinition.id;
          const fieldValue = value[definitionId];

          logger.info("Updating custom field", {
            definitionId,
            type: field.customFieldDefinition.type,
            isFile: fieldValue instanceof File,
            value: fieldValue instanceof File ? fieldValue.name : fieldValue,
          });

          return updateCustomFieldMutation.mutateAsync({
            definitionId,
            value: fieldValue,
            type: field.customFieldDefinition.type,
          });
        });

      try {
        await Promise.all(updatePromises || []);
      } catch (error) {
        // Errors already handled in mutation
        logger.error("Failed to update custom fields", error);
      }
    },
  });

  const renderField = (field: CustomField) => {
    const { customFieldDefinition } = field;
    const { id, name, description, type, required, options } = customFieldDefinition;

    switch (type) {
      case "TEXT":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field) => (
              <CustomFieldInput
                field={field}
                label={name}
                description={description}
                required={required}
              />
            )}
          />
        );

      case "NUMBER":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field) => (
              <CustomFieldInput
                field={field}
                label={name}
                description={description}
                type="number"
                required={required}
              />
            )}
          />
        );

      case "DATE":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field) => (
              <CustomFieldInput
                field={field}
                label={name}
                description={description}
                type="date"
                required={required}
              />
            )}
          />
        );

      case "SELECT":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field) => (
              <CustomFieldSelect
                field={field}
                label={name}
                description={description}
                options={options || []}
                required={required}
              />
            )}
          />
        );

      case "MULTI_SELECT":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field) => (
              <CustomFieldSelect
                field={field}
                label={name}
                description={description}
                options={options || []}
                multiple
                required={required}
              />
            )}
          />
        );

      case "BOOLEAN":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field) => (
              <CustomFieldToggle field={field} label={name} description={description} />
            )}
          />
        );

      case "FILE":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field) => (
              <CustomFieldFile
                field={field}
                label={name}
                description={description}
                required={required}
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <IonPage>
        <IonHeader>
          <Toolbar style={{ paddingTop: `calc(${statusBarHeight}px + 0.5rem)` }}>
            <Buttons slot="start">
              <BackButton defaultHref={Routes.Settings()} />
            </Buttons>
            <IonTitle>{t("routes:custom-fields.title")}</IonTitle>
          </Toolbar>
        </IonHeader>
        <Content>
          <div className="flex h-full items-center justify-center">
            <IonSpinner />
          </div>
        </Content>
      </IonPage>
    );
  }

  if (!customFields || customFields.length === 0) {
    return (
      <IonPage>
        <IonHeader>
          <Toolbar style={{ paddingTop: `calc(${statusBarHeight}px + 0.5rem)` }}>
            <Buttons slot="start">
              <BackButton defaultHref={Routes.Settings()} />
            </Buttons>
            <IonTitle>{t("routes:custom-fields.title")}</IonTitle>
          </Toolbar>
        </IonHeader>
        <Content>
          <div className="flex h-full items-center justify-center">
            <p className="text-txt-secondary">{t("routes:custom-fields.empty")}</p>
          </div>
        </Content>
      </IonPage>
    );
  }

  return (
    <FormElement>
      <IonPage>
        <IonHeader>
          <Toolbar style={{ paddingTop: `calc(${statusBarHeight}px + 0.5rem)` }}>
            <Buttons slot="start">
              <BackButton defaultHref={Routes.Settings()} />
            </Buttons>
            <IonTitle>{t("routes:custom-fields.title")}</IonTitle>
          </Toolbar>
        </IonHeader>
        <Content>
          <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
            <IonRefresherContent refreshingSpinner="circles" />
          </IonRefresher>
          <div className="flex flex-1 flex-col gap-6">
            {customFields.map((field) => renderField(field))}
          </div>
        </Content>
        <IonFooter>
          <Toolbar>
            <form.SubmitButton />
          </Toolbar>
        </IonFooter>
      </IonPage>
    </FormElement>
  );
}
